import { useState } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  CalendarDays,
  ChevronRight,
  Inbox,
  LayoutGrid,
  ListChecks,
  LogOut,
  Minus,
  Moon,
  Plus,
  Search,
  Settings,
  Sun,
} from 'lucide-react';
import type { Project } from '@shared/types';
import { cn } from '@/lib/format';
import { useLogout, useMe, useProjects } from '@/lib/queries';
import { Avatar, Spinner } from './ui';
import { useTheme } from '@/lib/theme';
import { useUiScale } from '@/lib/uiscale';

/**
 * The legacy sidebar listed all 52 projects flat, including one "Inbox" per
 * user, which made it unusable. This one renders the real parent/child tree
 * and collapses by default below the top level.
 */
export function Sidebar({ onClose }: { onClose?: () => void }) {
  const { data, isLoading } = useProjects();
  const { data: me } = useMe();
  const logout = useLogout();
  const { theme, toggle } = useTheme();
  const scale = useUiScale();

  return (
    <nav
      className="flex h-full w-64 shrink-0 flex-col bg-brand-800 text-brand-50/80"
      aria-label="Hauptnavigation"
    >
      <div className="flex h-14 items-center border-b border-white/10 px-3">
        {/* The logo's wordmark is dark green and won't read on the dark sidebar,
            so it sits on a small white rounded plate — same logo, legible. */}
        <div className="flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5">
          <img src="/ecotone-logo.jpeg" alt="Ecotone Tasks" className="h-6 w-auto" />
        </div>
      </div>

      <div className="space-y-0.5 p-2">
        <NavItem to="/" icon={<ListChecks />} label="Meine Aufgaben" onClick={onClose} />
        <NavItem to="/search" icon={<Search />} label="Suche" onClick={onClose} />
        <NavItem to="/calendar" icon={<CalendarDays />} label="Kalender" onClick={onClose} />
        <NavItem to="/inbox" icon={<Inbox />} label="Inbox" onClick={onClose} />
        <NavItem to="/holidays" icon={<Sun />} label="Urlaubsplan" onClick={onClose} />
      </div>

      <div className="mt-2 px-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
        Projekte
      </div>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner className="text-white/40" />
          </div>
        ) : (
          (data?.tree ?? []).map((p) => (
            <ProjectNode key={p.id} project={p} depth={0} onNavigate={onClose} />
          ))
        )}
      </div>

      <div className="border-t border-white/10 p-2">
        {/* Text-size control: scales the whole UI via the root font-size. */}
        <div className="mb-1 flex items-center gap-1 rounded-lg px-2 py-1">
          <span className="mr-auto text-[11px] text-white/50">Textgröße</span>
          <button
            onClick={scale.decrease}
            disabled={!scale.canDecrease}
            className="grid size-6 place-items-center rounded text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            aria-label="Text verkleinern"
          >
            <Minus className="size-3.5" />
          </button>
          <button
            onClick={scale.reset}
            className="min-w-9 rounded px-1 text-center text-[11px] tabular-nums text-white/70 hover:bg-white/10 hover:text-white"
            aria-label="Textgröße zurücksetzen"
            title="Zurücksetzen"
          >
            {scale.percent}%
          </button>
          <button
            onClick={scale.increase}
            disabled={!scale.canIncrease}
            className="grid size-6 place-items-center rounded text-white/60 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:hover:bg-transparent"
            aria-label="Text vergrößern"
          >
            <Plus className="size-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-2 rounded-lg px-2 py-1.5">
          {me && <Avatar user={me} size="sm" />}
          <span className="min-w-0 flex-1 truncate text-xs text-white/80">
            {me?.name ?? '…'}
          </span>
          <Link
            to="/settings"
            onClick={onClose}
            className="grid size-7 place-items-center rounded text-white/50 transition-colors hover:bg-white/10 hover:text-white [&.active]:text-white"
            aria-label="Einstellungen"
            title="Einstellungen"
          >
            <Settings className="size-4" />
          </Link>
          <button
            onClick={toggle}
            className="grid size-7 place-items-center rounded text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            aria-label={theme === 'dark' ? 'Zu hellem Design wechseln' : 'Zu dunklem Design wechseln'}
          >
            {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <button
            onClick={() => {
              logout.mutate(undefined, {
                // Full reload so no component keeps stale user state.
                onSettled: () => window.location.assign('/login'),
              });
            }}
            className="grid size-7 place-items-center rounded text-white/50 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Abmelden"
          >
            <LogOut className="size-4" />
          </button>
        </div>
      </div>
    </nav>
  );
}

function NavItem({
  to,
  icon,
  label,
  onClick,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-[13px] transition-colors
                 hover:bg-white/10 hover:text-white [&.active]:bg-white/20 [&.active]:text-white
                 [&>svg]:size-4 [&>svg]:shrink-0"
      activeOptions={{ exact: true }}
    >
      {icon}
      {label}
    </Link>
  );
}

function ProjectNode({
  project,
  depth,
  onNavigate,
}: {
  project: Project;
  depth: number;
  onNavigate?: () => void;
}) {
  const children = project.children ?? [];
  const hasChildren = children.length > 0;

  const routerState = useRouterState();
  const path = routerState.location.pathname;
  /** Keep an ancestor expanded when a descendant is the active route. */
  const containsActive = hasChildren && subtreeIds(project).some((id) => path.includes(`/projects/${id}`));

  const [expanded, setExpanded] = useState(depth === 0 || containsActive);

  return (
    <div>
      <div
        className="group flex items-center rounded-lg transition-colors hover:bg-white/10"
        // Indent by depth, capped so a deep tree can't push titles off-screen.
        style={{ paddingLeft: `${Math.min(depth, 3) * 0.75}rem` }}
      >
        {hasChildren ? (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="grid size-6 shrink-0 place-items-center text-white/40 hover:text-white"
            aria-label={expanded ? 'Unterprojekte einklappen' : 'Unterprojekte ausklappen'}
            aria-expanded={expanded}
          >
            <ChevronRight
              className={cn('size-3.5 transition-transform', expanded && 'rotate-90')}
            />
          </button>
        ) : (
          <span className="size-6 shrink-0" aria-hidden />
        )}

        <Link
          to="/projects/$projectId"
          params={{ projectId: project.id }}
          onClick={onNavigate}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pr-2 text-[13px]
                     hover:text-white [&.active]:font-medium [&.active]:text-white"
          title={project.title}
        >
          {project.hexColor ? (
            <span
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: project.hexColor }}
              aria-hidden
            />
          ) : (
            <LayoutGrid className="size-3.5 shrink-0 text-white/40" aria-hidden />
          )}
          <span className="truncate">{project.title}</span>
        </Link>
      </div>

      {hasChildren && expanded && (
        <div>
          {children.map((child) => (
            <ProjectNode
              key={child.id}
              project={child}
              depth={depth + 1}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Every project id in this subtree, including the root's own. */
function subtreeIds(project: Project): number[] {
  const out = [project.id];
  for (const child of project.children ?? []) out.push(...subtreeIds(child));
  return out;
}
